import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from 'ish-core/services/api/api.service';

export interface AccessRequestPayload {
  erpCustomerNo: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

/** PROVISIONAL — early email-domain check after ERP verify (FR1.3.8). No allow-list in response. */
export interface AccessRequestEmailDomainCheckResponse {
  ok: boolean;
}

/** PROVISIONAL — confirm with ICM. route models FR 1.2.7 / 1.2.1 outcomes. */
export type AccessRequestRoute = 'admin' | 'sales';

export interface AccessRequestResponse {
  ok: boolean;
  /** admin = pending Customer Admin approval; sales = notified + auto-processed (no shopping). */
  route: AccessRequestRoute;
  /** Discovery prototype: request stays pending until approved. */
  pendingApproval?: boolean;
  reviewerLabel?: string;
  detailKey?: string;
  /** i18n key preferred; plain message allowed from provisional backends */
  messageKey?: string;
  message?: string;
}

/**
 * PROVISIONAL — confirm ERP exists before collecting remaining registration fields.
 * Expected: `POST accessrequests/verify` `{ erpCustomerNo }` → account summary.
 */
export interface AccessRequestErpVerifyResponse {
  ok: boolean;
  erpCustomerNo: string;
  /** Display name for the verified customer account when available. */
  companyName?: string;
  hasAdministrators?: boolean;
  hasAssignedCsr?: boolean;
  /** When true, the requester should contact their Presidio sales representative instead of continuing. */
  contactSalesRep?: boolean;
}

/**
 * Request access to an existing ERP/customer account (unauthenticated).
 * PROVISIONAL contract: `POST accessrequests` / `POST accessrequests/verify` — confirm with ICM.
 */
@Injectable({ providedIn: 'root' })
export class AccessRequestService {
  constructor(private apiService: ApiService) {}

  verifyErp(erpCustomerNo: string): Observable<AccessRequestErpVerifyResponse> {
    return this.apiService.post<AccessRequestErpVerifyResponse>('accessrequests/verify', {
      erpCustomerNo,
    });
  }

  /**
   * Check whether the email domain is allowed for the verified ERP account.
   * PROVISIONAL: `POST accessrequests/check-email` — confirm with ICM.
   */
  checkEmailDomain(
    erpCustomerNo: string,
    email: string
  ): Observable<AccessRequestEmailDomainCheckResponse> {
    return this.apiService.post<AccessRequestEmailDomainCheckResponse>('accessrequests/check-email', {
      erpCustomerNo,
      email,
    });
  }

  requestAccess(payload: AccessRequestPayload): Observable<AccessRequestResponse> {
    return this.apiService.post<AccessRequestResponse>('accessrequests', payload);
  }
}
